if (typeof debounce === 'undefined') {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}
/**
 *  @class
 *  @function ThemeHeader
 */

if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    constructor() {
      super();

    }
    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = this.querySelector('.mobile-toggle-wrapper');
      this.headerLayoutEvents?.abort();
      this.headerLayoutEvents = new AbortController();
      const signal = this.headerLayoutEvents.signal;
      this.desktopHeader = window.matchMedia('(min-width: 1068px)');

      // Modal Escape and return focus are owned by ivoodoo-ui-components.js.
      if (this.classList.contains('header-sticky--active')) {
        document.body.classList.add('header-sticky--active');
      }
      this.toggle.querySelector('.mobile-toggle').addEventListener('click', (e) => {
        if (this.toggle.classList.contains('active')) {
          e.preventDefault();
          document.body.classList.remove('overflow-hidden');
          this.toggle.classList.remove('active');
          this.closeAnimation(this.toggle);
        } else {
          document.body.classList.add('overflow-hidden');
          setTimeout(() => {
            this.toggle.classList.add('active');
          });
        }
      }, { signal });

      // One geometry update owns both the visible height and the reserved flow space.
      window.addEventListener('scroll', () => this.updateHeaderLayout(), { passive: true, signal });
      window.addEventListener('resize', () => this.updateHeaderLayout(true), { passive: true, signal });
      const updateAfterFocus = () => {
        cancelAnimationFrame(this.headerFocusFrame);
        this.headerFocusFrame = requestAnimationFrame(() => this.updateHeaderLayout());
      };
      this.addEventListener('focusin', updateAfterFocus, { signal });
      this.addEventListener('focusout', updateAfterFocus, { signal });
      this.headerSizeObserver?.disconnect();
      this.headerSizeObserver = new ResizeObserver(() => this.updateHeaderLayout());
      this.headerSizeObserver.observe(this);
      const announcement = document.querySelector('.announcement-bar-section');
      if (announcement) this.headerSizeObserver.observe(announcement);
      this.updateHeaderLayout(true);
      document.fonts.ready.then(() => {
        if (this.isConnected && !signal.aborted) this.updateHeaderLayout(true);
      });

      // Mobile Navigation transparent header support.
      setTimeout(() => {

        this.mobile_nav = document.querySelector('.header-mobile-navigation');

        if (this.mobile_nav && this.classList.contains('transparent--true')) {
          this.mobile_nav.classList.add('is-fixed');
        }
      }, 100);
      // Buttons.
      this.menu.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this), { signal }));
      this.menu.querySelectorAll('.parent-link-back--button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this), { signal }));
    }
    disconnectedCallback() {
      this.headerLayoutEvents?.abort();
      this.headerSizeObserver?.disconnect();
      cancelAnimationFrame(this.headerFocusFrame);
    }
    updateHeaderLayout(remeasure = false) {
      this.setStickyClass();
      const desktopSticky = this.desktopHeader.matches && this.classList.contains('style5') && this.classList.contains('header-sticky--active');
      const reserve = desktopSticky && !this.classList.contains('transparent--true');
      const wasCompact = this.classList.contains('iv-header-compact');
      if (reserve && (remeasure || !wasCompact || !this.expandedHeaderHeight)) {
        // Measure at the current width, then restore the compact state before paint.
        this.classList.remove('iv-header-compact');
        this.expandedHeaderHeight = this.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--header-flow-height', this.expandedHeaderHeight + 'px');
      }
      if (!reserve) {
        this.expandedHeaderHeight = null;
        document.documentElement.style.removeProperty('--header-flow-height');
      }
      this.header_section.classList.toggle('iv-header-reserved', reserve);
      const searchFocused = this.querySelector('.searchform')?.contains(document.activeElement);
      this.classList.toggle('iv-header-compact', desktopSticky && this.classList.contains('is-sticky') && !searchFocused);
      this.setHeaderOffset();
      this.setHeaderHeight();
      this.setAnnouncementHeight();
    }
    setStickyClass() {
      if (this.classList.contains('header-sticky--active')) {
        let offset = parseInt(this.getBoundingClientRect().top, 10) + document.documentElement.scrollTop;
        this.classList.toggle('is-sticky', window.scrollY >= offset && window.scrollY > 0);
      }
    }
    setAnnouncementHeight() {
      const a_bar = document.querySelector('.announcement-bar-section');
      let h = a_bar ? a_bar.clientHeight : 0;
      document.documentElement.style.setProperty('--announcement-height', h + 'px');
    }
    setHeaderOffset() {
      let h = this.header_section.getBoundingClientRect().top;
      document.documentElement.style.setProperty('--header-offset', h + 'px');
    }
    setHeaderHeight() {
      let h = this.clientHeight;
      document.documentElement.style.setProperty('--header-height', h + 'px');
    }
    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentMenuElement = detailsElement.closest('.link-container');
      const isOpen = detailsElement.hasAttribute('open');
      if (this.querySelector('.parent-link-back--button')) {
        this.menu.scrollTop = 0;
      }
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
      }, 100);
    }
    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }
    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      this.closeAnimation(detailsElement);
    }
    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }
  customElements.define('theme-header', ThemeHeader);
}

/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();

      this.submenus = this.querySelectorAll('.thb-full-menu>.menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu');
    }
    connectedCallback() {
      if (!this.submenus.length) {
        return;
      }
      const _this = this;
      // resize on initial load
      document.fonts.ready.then(function() {
        window.addEventListener('resize', debounce(function() {
          _this.resizeSubMenus();
        }, 100));
      });

    }
    resizeSubMenus() {
      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(':scope >.menu-item-has-children>.sub-menu');

        sub_submenus.forEach((sub_submenu) => {
          let w = sub_submenu.offsetWidth,
            l = sub_submenu.parentElement.getBoundingClientRect().left + sub_submenu.parentElement.offsetWidth,
            total = w + l;

          if (total > window.innerWidth) {
            sub_submenu.parentElement.classList.add('left-submenu');
          } else if (sub_submenu.parentElement.classList.contains('left-submenu')) {
            sub_submenu.parentElement.classList.remove('left-submenu');
          }
        });
      });
    }
  }
  customElements.define('full-menu', FullMenu);
}
